import "components/Map/Map.css";
import "leaflet/dist/leaflet.css";
import Map from "components/Map/Map";
import PageLayout from "layouts/PageLayout/PageLayout";
import { useParams } from "react-router-dom";
import { ProjectMap } from "generated/graphql";
import { useCustomGetProjectMapLazyQuery } from "generated/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import useProjectMapMeasurements from "hooks/projectMaps/useProjectMapMeasurements";
import useUpdateProjectMap from "hooks/projectMaps/useUpdateProjectMap";
import MeasurementsSidebar from "modules/proposals/draft/measurements/sidebar/MeasurementsSidebar";
import CreateProposalFromMapDialog from "modules/projectMaps/CreateProposalFromMapDialog";
import {
  mapAddressToUpdateProjectMapInput,
  projectMapToMapAddress,
} from "modules/projectMaps/projectMapAddress";
import { normalizeStageMapData } from "modules/proposals/draft/measurements/stageMeasurements";
import { MapAddress } from "interfaces/interfaces";
import type { FeatureCollection } from "geojson";

const isTouchDevice = (): boolean => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0
  );
};

const useTouchControls = (): boolean => {
  const isMobile = isTouchDevice();
  return isMobile;
};

const isTouch = useTouchControls();

export default function MapPage() {
  const { mapId } = useParams<{ mapId: string }>();

  const [getProjectMap, { data: mapQueryData, loading }] = useCustomGetProjectMapLazyQuery();

  useEffect(() => {
    if (mapId) {
      getProjectMap({ variables: { id: mapId } });
    }
  }, [mapId, getProjectMap]);

  const projectMap = mapQueryData?.getProjectMap ?? null;

  const measurements = useProjectMapMeasurements({ projectMap });
  const [updateProjectMap] = useUpdateProjectMap();

  const initialAddress = useMemo(
    () => projectMapToMapAddress(projectMap),
    [
      projectMap?.id,
      projectMap?.addressLine1,
      projectMap?.addressLine2,
      projectMap?.addressCity,
      projectMap?.addressState,
      projectMap?.addressZip,
      projectMap?.addressCountry,
      projectMap?.latitude,
      projectMap?.longitude,
    ]
  );

  const handleAddressChange = useCallback(
    async (address: MapAddress | null) => {
      if (!projectMap?.id || !address?.line1) {
        return;
      }

      await updateProjectMap({
        variables: {
          input: mapAddressToUpdateProjectMapInput(projectMap.id, address),
        },
      });
    },
    [projectMap?.id, updateProjectMap]
  );

  const initialFeatures = useMemo(() => {
    if (!projectMap?.mapData) return null;
    try {
      const parsed = JSON.parse(projectMap.mapData) as FeatureCollection;
      return normalizeStageMapData(parsed);
    } catch {
      return null;
    }
  }, [projectMap?.mapData]);

  const initialCenter = useMemo((): [number, number] | null => {
    if (projectMap?.latitude != null && projectMap?.longitude != null) {
      return [projectMap.latitude, projectMap.longitude];
    }
    return null;
  }, [projectMap?.latitude, projectMap?.longitude]);

  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);

  const createIslandConfirmation = (callback: () => void): boolean => {
    if (confirm("Do you want to create an island?")) {
      callback();
      return true;
    }

    return false;
  };

  if (loading) {
    return (
      <PageLayout>
        <div style={{ padding: "24px", textAlign: "center" }}>Loading map...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ position: "relative", height: "100vh", overflow: "hidden" }}>
        <Map
          readonly={false}
          createIslandConfirmation={createIslandConfirmation}
          initialFeatures={initialFeatures}
          initialCenter={initialCenter}
          guidedControls={window.location.search.indexOf("guided") !== -1}
          isTouch={isTouch}
          goBack
          goBackTo="/map"
          goBackLabel="Back to Maps"
          onFeatureSelect={measurements.handleFeatureSelect}
          onChange={measurements.handleMapChange}
          highlightedFeatureId={measurements.selectedFeatureId}
          initialAddress={initialAddress}
          onAddressChange={handleAddressChange}
        />
      </div>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1500,
          pointerEvents: "none",
        }}
      >
        <div style={{ pointerEvents: "auto", position: "absolute", top: 0, right: 0, bottom: 0 }}>
          <MeasurementsSidebar
            isOpen={measurements.isDrawerOpen}
            onToggle={measurements.toggleDrawer}
            measurements={measurements.measurements}
            selectedFeatureId={measurements.selectedFeatureId}
            onSelectFeature={measurements.selectFeature}
            products={measurements.products}
            groupedAssignments={measurements.groupedAssignments}
            onUpdateProduct={measurements.updateMeasurementProduct}
            onToggleScreenshot={measurements.toggleScreenshot}
            onSave={measurements.save}
            onApply={measurements.save}
            persisting={measurements.persisting}
            isDirty={measurements.isDirty}
            applyLoading={false}
            productsLoading={measurements.productsLoading}
            applyError={null}
            canSave={measurements.canSave}
            hideApply
            onCreateProposal={async () => {
              await measurements.save();
              setProposalDialogOpen(true);
            }}
          />
        </div>
      </div>
      {projectMap && (
        <CreateProposalFromMapDialog
          open={proposalDialogOpen}
          onClose={() => setProposalDialogOpen(false)}
          projectMap={projectMap as unknown as ProjectMap}
        />
      )}
    </PageLayout>
  );
}
